import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { highLevelMetrics } from 'api/CampaignIndexAPI';
import { EmbeddedMessage, MessageMedium } from 'constants/messageMediums';
import { Skeleton } from 'ui/skeleton';
import { colors, fontSizes } from 'ui/theme';

import { TableCell } from '../CampaignIndexTable.styles';
import { Campaign } from '../domainTypes';
import { selectCampaignMetrics, selectCampaignMetricsLoading } from '../selectors';
import { parseEngagementMetricPercentage, shouldDisplayCampaignMetrics } from '../utils';

const PrimaryCaptionText = styled.div`
  color: ${colors.textPrimary};
  font-size: ${fontSizes.s12};
  line-height: ${fontSizes.s16};
`;

const SecondaryCaptionText = styled(PrimaryCaptionText)`
  color: ${colors.textSecondary};
`;

const DefaultText = styled(PrimaryCaptionText)`
  color: ${colors.grey50};
`;

type Props = {
  campaign: Campaign;
};

const generateKPI = (
  id: Campaign['id'],
  messageMedium: MessageMedium,
  campaignMetrics: any = {},
) => {
  // metrics
  const totalClicks = campaignMetrics[highLevelMetrics.totalClicks.value]?.toLocaleString() || 0;

  // rates
  const clickRate = campaignMetrics[highLevelMetrics.clickRate.value] || 0;
  const openRate = campaignMetrics[highLevelMetrics.openRate.value] || 0;

  const totalClicksText = (
    <PrimaryCaptionText data-test="campaign-engagement-cell-total-clicks">
      {`${totalClicks} total ${totalClicks === 1 ? 'click' : 'clicks'} `}
    </PrimaryCaptionText>
  );

  const openRateText = (
    <PrimaryCaptionText data-test="campaign-engagement-cell-open-rate">
      {`${parseEngagementMetricPercentage(openRate)} open rate`}
    </PrimaryCaptionText>
  );

  const clickRateText = (
    <div data-test="campaign-engagement-cell-click-rate">
      {`${parseEngagementMetricPercentage(clickRate)} click rate`}
    </div>
  );

  const MediumComponents: Record<MessageMedium, JSX.Element> = {
    [MessageMedium.Email]: (
      <>
        {openRateText}
        <SecondaryCaptionText>{clickRateText}</SecondaryCaptionText>
      </>
    ),
    [MessageMedium.Push]: openRateText,
    [MessageMedium.SMS]: <PrimaryCaptionText>{clickRateText}</PrimaryCaptionText>,
    [MessageMedium.InApp]: totalClicksText,
    [MessageMedium.WebPush]: totalClicksText,
    [EmbeddedMessage]: totalClicksText,
  };

  return MediumComponents[messageMedium as MessageMedium] || null;
};

const renderLoadingOrFailed = (id: Campaign['id'], isLoading: boolean) => {
  return isLoading ? (
    <Skeleton />
  ) : (
    <div>
      <SecondaryCaptionText data-test="campaign-engagement-cell-metrics-failure">
        Metrics not loading
      </SecondaryCaptionText>
    </div>
  );
};

const generateCellContent = (campaign: Campaign, campaignMetrics: any, isLoading: boolean) => {
  const { id } = campaign;
  const metrics = campaignMetrics[id] || {};
  const isDefined =
    metrics[highLevelMetrics.totalSent.value] !== undefined ||
    metrics[highLevelMetrics.totalRetrieved.value] !== undefined;

  if (shouldDisplayCampaignMetrics(campaign)) {
    return isDefined ? (
      <div>{generateKPI(id, campaign.messageMedium, metrics)}</div>
    ) : (
      renderLoadingOrFailed(id, isLoading)
    );
  }

  // when campaign has not sent
  return <DefaultText data-test="campaign-engagement-cell-default">N/A</DefaultText>;
};

export const CampaignEngagementTableCell = ({ campaign }: Props) => {
  const campaignMetrics = useSelector(selectCampaignMetrics);
  const campaignMetricsLoading = useSelector(selectCampaignMetricsLoading);

  return (
    <TableCell data-test="campaign-index-engagement-cell">
      {generateCellContent(campaign, campaignMetrics, campaignMetricsLoading)}
    </TableCell>
  );
};
